/* eslint-disable react-hooks/exhaustive-deps */
import {
    Box,
    Flex,
    VStack,
    HStack,
    SimpleGrid,
    Text,
    Button,
    Input,
    Textarea,
    Select,
    Image,
    Badge,
    Divider,
    Radio,
    RadioGroup,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Spinner,
    Center,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Paystack from '@paystack/inline-js';
import useGetUserDetails from 'hooks/useGetUserDetail';
import { useSampleRequestMutations } from 'hooks/useSampleRequest';
import { useUSDToNairaCurrency } from 'hooks/useCurrency';
import { useGetQoutesAndCarriers } from 'hooks/useGetLocations';
import WalletHistoryPrice from 'components/Wallet/WalletHistoryPrice';
import PlaceHolder from 'assets/placeholder.png';
import CustomerStep from 'components/CustomerStep';
import { formatPhoneNumberInternational } from 'utils/phoneformatter';
import isoCountries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

isoCountries.registerLocale(en);

const NAVY = '#1B3B5F';
const NAVY2 = '#162f4e';
const TEAL = '#0D9488';
const SLATE = '#F8FAFC';
const BORDER = '#E2E8F0';

const STORE_OPEBI = {
    carrier_id: 'opebi',
    name: 'Warehouse Pickup – Opebi',
    address: {
        line1: '77 Opebi Road',
        city: 'Ikeja',
        state: { label: 'Lagos', value: 'Lagos' },
        country: { label: 'NG', value: 'Nigeria' },
        phone: '+234800335245',
    },
    amount: 0,
    delivery_time: 'Same-day Pickup',
    carrier_rate_description: 'Fulfilment Centre Pickup',
};

const resolveImage = (item) => {
    if (!item) return null;
    const cf = item.cf_singleimage ?? item.custom_field_hash?.cf_singleimage;
    if (cf) {
        try {
            const p = JSON.parse(cf);
            if (Array.isArray(p) && p[0]) return p[0];
            if (typeof p === 'string' && p.startsWith('http')) return p;
        } catch {
            if (typeof cf === 'string' && cf.startsWith('http')) return cf;
        }
    }
    return item.image_name ?? item.image ?? null;
};

const FieldLabel = ({ children }) => (
    <Text
        fontSize="xs"
        fontWeight="700"
        color="gray.600"
        textTransform="uppercase"
        letterSpacing="0.04em"
        mb={1}>
        {children}
    </Text>
);

const StepBar = ({ current, steps }) => (
    <Flex align="center" gap={0} mb={5} px={1}>
        {steps.map((label, i) => {
            const n = i + 1;
            const done = current > n;
            const active = current === n;
            return (
                <Flex key={label} align="center" flex={i < steps.length - 1 ? 1 : 0}>
                    <Flex direction="column" align="center" gap="3px" minW="48px">
                        <Flex
                            w="26px"
                            h="26px"
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg={done || active ? NAVY : 'gray.200'}
                            color={done || active ? 'white' : 'gray.400'}
                            fontSize="10px"
                            fontWeight="700"
                            transition="all 0.2s"
                            flexShrink={0}>
                            {done ? '✓' : n}
                        </Flex>
                        <Text
                            fontSize="9px"
                            fontWeight={active ? '700' : '400'}
                            color={active ? NAVY : 'gray.400'}
                            textAlign="center"
                            whiteSpace="nowrap">
                            {label}
                        </Text>
                    </Flex>
                    {i < steps.length - 1 && (
                        <Box
                            flex={1}
                            h="1px"
                            bg={current > n ? NAVY : BORDER}
                            mx={1}
                            transition="all 0.3s"
                            mb="14px"
                        />
                    )}
                </Flex>
            );
        })}
    </Flex>
);

// ── Dispatch inner step (same logic as DispatchStep in SampleRequest.jsx) ─────
const DispatchStep = ({ deliveryAddress, cartItems, onConfirm, onBack }) => {
    const { routeMatch, isLoading: carriersLoading } = useGetQoutesAndCarriers();

    const [choice, setChoice] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [fetchDone, setFetchDone] = useState(false);
    const [noCarriers, setNoCarriers] = useState(false);
    const [shipments, setShipments] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const normalizePickupAddr = (addr = {}) => ({
        line1: (addr.line1 || '').trim().toLowerCase(),
        city: (addr.city || '').trim().toLowerCase(),
        state: (addr.state || '').trim().toLowerCase(),
        country: (addr.country || '').trim().toUpperCase(),
    });

    const groupItems = (items = []) => {
        const groups = {};
        items.forEach((item) => {
            const countryCode =
                isoCountries.getAlpha2Code(item.cf_country, 'en') || item.cf_country || 'NG';
            const key = JSON.stringify(
                normalizePickupAddr({
                    line1: item.line_address,
                    city: item.cf_city,
                    state: item.cf_state,
                    country: countryCode,
                }),
            );
            const phone =
                formatPhoneNumberInternational(item?.phone, countryCode)?.split(' ').join('') || '';
            if (!groups[key]) {
                groups[key] = {
                    pickup_address: {
                        line1: item.line_address,
                        city: item.cf_city,
                        state: item.cf_state,
                        country: countryCode,
                        phone,
                    },
                    items: [],
                };
            }
            groups[key].items.push({
                name: item?.name,
                description: item?.description ?? item?.name,
                currency: 'NGN',
                value: item?.total_price ?? 0,
                weight: item?.cf_weight ?? 1,
                item_id: item?.item_id,
                quantity: item?.quantity ?? 1,
                pickup_address: groups[key].pickup_address,
            });
        });
        return Object.values(groups).flatMap((g) => g.items);
    };

    const formatStoreAddr = () => ({
        first_name: 'Obana',
        last_name: 'Africa',
        email: 'obana.africa@gmail.com',
        phone: STORE_OPEBI.address.phone,
        is_residential: false,
        line1: STORE_OPEBI.address.line1,
        city: STORE_OPEBI.address.city,
        state: STORE_OPEBI.address.state?.value,
        country: STORE_OPEBI.address.country?.label,
        zip: '100001',
    });

    const buildDeliveryAddr = () => {
        if (!deliveryAddress) return formatStoreAddr();
        const countryCode =
            typeof deliveryAddress.country === 'object'
                ? deliveryAddress.country?.value ?? deliveryAddress.country?.label
                : deliveryAddress.country;
        return {
            ...deliveryAddress,
            country: countryCode,
            phone:
                formatPhoneNumberInternational(deliveryAddress.phone, countryCode)
                    ?.split(' ')
                    .join('') || deliveryAddress.phone,
        };
    };

    const deliveryAddrLabel = (() => {
        if (!deliveryAddress) return 'Your delivery address';
        if (typeof deliveryAddress === 'string') return deliveryAddress;
        return (
            [
                deliveryAddress?.line1,
                deliveryAddress?.city,
                deliveryAddress?.state?.label ?? deliveryAddress?.state,
            ]
                .filter(Boolean)
                .join(', ') || 'Your delivery address'
        );
    })();

    const fetchShipments = async (type) => {
        setFetching(true);
        setFetchDone(false);
        setNoCarriers(false);
        setShipments([]);

        const isPickup = type === 'pickup';
        const delivAddr = isPickup ? formatStoreAddr() : buildDeliveryAddr();
        const FALLBACK = { line1: '77 opebi road', city: 'ikeja', state: 'lagos', country: 'NG' };

        const validatedItems = (cartItems || []).map((item) => ({
            ...item,
            line_address: item.line_address || FALLBACK.line1,
            cf_city: item.cf_city || FALLBACK.city,
            cf_state: item.cf_state || FALLBACK.state,
            cf_country: item.cf_country || FALLBACK.country,
        }));

        const payload = {
            delivery_address: delivAddr,
            currency: 'NGN',
            parcel: {
                description: 'obana logistics shipment',
                packaging: '',
                weight_unit: 'kg',
                items: groupItems(validatedItems),
            },
        };

        try {
            const result = await routeMatch(payload);
            const results = Array.isArray(result) ? result : result ? [result] : [];
            if (!results.length) setNoCarriers(true);
            else setShipments(results);
        } catch (err) {
            console.error('DispatchStep fetchShipments:', err);
            setNoCarriers(true);
        } finally {
            setFetching(false);
            setFetchDone(true);
        }
    };

    const handlePickup = () => {
        setChoice('pickup');
        onConfirm({
            carrier_id: STORE_OPEBI.carrier_id,
            carrier_name: STORE_OPEBI.name,
            amount: 0,
            delivery_time: STORE_OPEBI.delivery_time,
            carrier_rate_description: STORE_OPEBI.carrier_rate_description,
            dispatchType: 'pickup',
            delivery_type: 'pickup',
        });
    };

    const handleDoorDelivery = async () => {
        setChoice('delivery');
        setShowModal(true);
        await fetchShipments('delivery');
    };

    const totalAmount = shipments.reduce((s, sh) => s + (sh.amount || 0), 0);

    const handleConfirmDelivery = () => {
        onConfirm({
            carrier_id: 'obana_logistics',
            carrier_name: 'Obana Logistics',
            amount: totalAmount,
            delivery_time:
                shipments.length > 1
                    ? 'Multiple shipments'
                    : shipments[0]?.delivery_time || 'Obana Logistics delivery',
            carrier_rate_description:
                shipments.length > 1
                    ? `${shipments.length} grouped shipments`
                    : shipments[0]?.carrier_rate_description || 'Obana Logistics delivery',
            shipments,
            dispatchType: 'delivery',
            delivery_type: shipments.length > 1 ? 'multi-shipment' : 'single-shipment',
            isMultiVendor: shipments.length > 1,
        });
        setShowModal(false);
    };

    return (
        <Box>
            <Text
                fontWeight="700"
                color={NAVY}
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="0.06em"
                mb={4}>
                Choose Delivery Method
            </Text>

            <VStack spacing={3} mb={5} align="stretch">
                {/* Pickup */}
                <Box
                    border="2px solid"
                    borderColor={choice === 'pickup' ? NAVY : BORDER}
                    borderRadius="xl"
                    p={4}
                    cursor="pointer"
                    bg={choice === 'pickup' ? '#EEF3F9' : 'white'}
                    _hover={{ borderColor: NAVY, bg: '#EEF3F9' }}
                    transition="all 0.15s"
                    onClick={handlePickup}>
                    <HStack spacing={3} align="start">
                        <Text fontSize="lg" flexShrink={0}>
                            🏢
                        </Text>
                        <Box flex={1}>
                            <Text fontWeight="700" fontSize="sm" color={NAVY}>
                                Fulfilment Centre Pickup
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt="2px">
                                {STORE_OPEBI.address.line1}, {STORE_OPEBI.address.city},{' '}
                                {STORE_OPEBI.address.state?.label}
                            </Text>
                            <Text fontSize="xs" color={TEAL} fontWeight="600" mt="2px">
                                Free · Same-day collection
                            </Text>
                        </Box>
                        {choice === 'pickup' && (
                            <CheckCircleIcon color={NAVY} boxSize={4} flexShrink={0} />
                        )}
                    </HStack>
                </Box>

                <Box
                    border="2px solid"
                    borderColor={choice === 'delivery' ? NAVY : BORDER}
                    borderRadius="xl"
                    p={4}
                    cursor="pointer"
                    bg={choice === 'delivery' ? '#EEF3F9' : 'white'}
                    _hover={{ borderColor: NAVY, bg: '#EEF3F9' }}
                    transition="all 0.15s"
                    onClick={handleDoorDelivery}>
                    <HStack spacing={3} align="start">
                        <Text fontSize="lg" flexShrink={0}>
                            🚚
                        </Text>
                        <Box flex={1}>
                            <Text fontWeight="700" fontSize="sm" color={NAVY}>
                                Door Delivery
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt="2px" noOfLines={2}>
                                {deliveryAddrLabel}
                            </Text>
                            <Text fontSize="xs" color="gray.400" mt="2px">
                                Via Obana Logistics
                            </Text>
                        </Box>
                        {choice === 'delivery' && (
                            <CheckCircleIcon color={NAVY} boxSize={4} flexShrink={0} />
                        )}
                    </HStack>
                </Box>
            </VStack>

            <Button variant="outline" borderRadius="xl" size="sm" onClick={onBack}>
                ← Back
            </Button>

            <Modal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setChoice(null);
                }}
                size="full"
                isCentered
                scrollBehavior="inside">
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent
                    borderRadius={{ base: '2xl', md: '2xl' }}
                    border={`1px solid ${BORDER}`}
                    shadow="2xl"
                    maxW={{ base: '100vw', md: '480px' }}
                    maxH={{ base: '92vh', md: '200px' }}
                    mx={{ base: 2, md: 'auto' }}
                    my={{ base: 'auto', md: 'auto' }}>
                    <ModalHeader bg={NAVY} color="white" borderTopRadius="2xl" py={4} px={5}>
                        <Text fontSize="sm" fontWeight="800">
                            {fetchDone && !noCarriers
                                ? 'Obana Logistics Shipment Summary'
                                : 'Calculating Rates…'}
                        </Text>
                        <Text fontSize="xs" opacity={0.65} mt="1px" fontWeight="400">
                            {fetchDone && !noCarriers
                                ? 'Review your shipment and confirm'
                                : 'Please wait…'}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton color="white" top={3} />

                    <ModalBody px={5} py={4} overflowY="auto">
                        {fetching || carriersLoading ? (
                            <Center py={10} flexDirection="column" gap={3}>
                                <Spinner size="lg" color={NAVY} thickness="3px" />
                                <Text fontSize="sm" color="gray.500" textAlign="center">
                                    Fetching Obana Logistics shipment details…
                                </Text>
                            </Center>
                        ) : noCarriers ? (
                            <Center py={10}>
                                <VStack spacing={4} maxW="320px" textAlign="center">
                                    <Text fontSize="3xl">😕</Text>
                                    <Text fontWeight="700" color={NAVY}>
                                        No Carriers Available
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        Please verify your delivery address and try again.
                                    </Text>
                                    <Button
                                        variant="outline"
                                        borderRadius="xl"
                                        size="sm"
                                        onClick={() => {
                                            setShowModal(false);
                                            setChoice(null);
                                        }}>
                                        Go Back
                                    </Button>
                                </VStack>
                            </Center>
                        ) : (
                            <VStack spacing={3} align="stretch">
                                <Box
                                    bg="#EEF3F9"
                                    borderRadius="xl"
                                    p={4}
                                    border={`1px solid ${BORDER}`}>
                                    <Flex justify="space-between" align="center">
                                        <Box>
                                            <Text fontWeight="700" fontSize="sm" color={NAVY}>
                                                Total Delivery Cost
                                            </Text>
                                            <Text fontSize="xs" color="gray.500" mt="1px">
                                                {shipments.length > 1
                                                    ? `${shipments.length} grouped shipments`
                                                    : 'Single shipment'}
                                            </Text>
                                        </Box>
                                        <Text fontWeight="800" fontSize="xl" color={NAVY}>
                                            ₦{totalAmount.toLocaleString()}
                                        </Text>
                                    </Flex>
                                </Box>

                                {shipments.map((shipment, idx) => (
                                    <Box
                                        key={shipment.id || idx}
                                        bg="white"
                                        p={4}
                                        border={`1px solid ${BORDER}`}
                                        borderRadius="xl">
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontWeight="700" fontSize="sm" color={NAVY}>
                                                Shipment {idx + 1}
                                            </Text>
                                            <Text fontWeight="700" fontSize="sm" color={TEAL}>
                                                ₦{(shipment.amount || 0).toLocaleString()}
                                            </Text>
                                        </Flex>
                                        <VStack align="start" spacing={1}>
                                            {(shipment.pickup_address?.line1 ||
                                                shipment.pickup_address?.city) && (
                                                <Text fontSize="xs" color="gray.500">
                                                    Pickup:{' '}
                                                    {[
                                                        shipment.pickup_address?.line1,
                                                        shipment.pickup_address?.city,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ')}
                                                </Text>
                                            )}
                                            <Text fontSize="xs" color="gray.500">
                                                Items: {shipment.items?.length || 0}
                                            </Text>
                                            {shipment.delivery_time && (
                                                <Text fontSize="xs" color="gray.500">
                                                    Est. delivery: {shipment.delivery_time}
                                                </Text>
                                            )}
                                            {shipment.carrier_name && (
                                                <Text fontSize="xs" color="gray.500">
                                                    Carrier: {shipment.carrier_name}
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </ModalBody>

                    <Flex p={4} borderTop={`1px solid ${BORDER}`} gap={3}>
                        <Button
                            flex={1}
                            variant="outline"
                            borderRadius="xl"
                            size="sm"
                            onClick={() => {
                                setShowModal(false);
                                setChoice(null);
                            }}>
                            Cancel
                        </Button>
                        <Button
                            flex={1}
                            bg={NAVY}
                            color="white"
                            _hover={{ bg: NAVY2 }}
                            borderRadius="xl"
                            size="sm"
                            isDisabled={!fetchDone || noCarriers || !shipments.length}
                            onClick={handleConfirmDelivery}>
                            Confirm & Continue →
                        </Button>
                    </Flex>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default function ProductSampleRequestModal({ isOpen, onClose, product, itemGroup }) {
    const { flattenedUser } = useGetUserDetails();
    const { mutate, isPending } = useSampleRequestMutations();
    const selectedCurrency = useSelector((s) => s.currencyReducer.currenciesRates);
    const userAttrs = flattenedUser?.attributes || {};

    const productName = itemGroup?.group_name ?? product?.name ?? product?.item_name ?? '';
    const productImage = resolveImage(product) ?? null;
    const sampleAvailable = product?.custom_field_hash?.cf_sample_available === 'Yes';

    const rawSizes = product?.custom_field_hash?.cf_sizes_run || product?.cf_sizes_run || '';
    const rawColors = product?.custom_field_hash?.cf_color || product?.cf_color || '';
    const sizeOptions = rawSizes
        ? rawSizes
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
        : [];
    const colorOptions = rawColors
        ? rawColors
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
        : [];

    const unitPrice =
        product?.rate ??
        product?.cf_item_price_unformatted ??
        product?.custom_field_hash?.cf_item_price_unformatted ??
        0;

    // const STEPS = ['Customer', 'Product', 'Delivery', 'Payment'];

    const [step, setStep] = useState(0);
    const [customerContext, setCustomerContext] = useState(null);
    const [dispatcher, setDispatcher] = useState(null);
    const [done, setDone] = useState(false);

    const [form, setForm] = useState({
        qty: 1,
        reason: '',
        color: colorOptions[0] ?? '',
        size: sizeOptions[0] ?? '',
        paymentMethod: '',
    });

    useEffect(() => {
        if (!isOpen) return;
        setStep(0);
        setDone(false);
        setCustomerContext(null);
        setDispatcher(null);
        setForm({
            qty: 1,
            reason: '',
            color: colorOptions[0] ?? '',
            size: sizeOptions[0] ?? '',
            paymentMethod: '',
        });
    }, [isOpen]);

    const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

    const totalUSD = unitPrice * form.qty;
    const convertedTotal = totalUSD / (selectedCurrency?.sell ?? 1);
    const usdToNaira = useUSDToNairaCurrency(totalUSD);

    const deliveryAddress = (() => {
        if (!customerContext) return null;
        const addr = customerContext.delivery_address ?? {};
        return {
            first_name: addr.first_name ?? userAttrs?.first_name ?? '',
            last_name: addr.last_name ?? userAttrs?.last_name ?? '',
            email: addr.email || flattenedUser?.email || '',
            phone: addr.phone || flattenedUser?.phone || '',
            line1: addr.line1 || userAttrs?.address || '',
            city: addr.city || userAttrs?.city || '',
            state: addr.state || userAttrs?.state || '',
            country: addr.country || userAttrs?.country || '',
            is_residential: true,
        };
    })();

    const cartItemsForDispatch = [
        {
            name: productName,
            description: productName,
            total_price: unitPrice ?? 0,
            cf_weight: product?.cf_weight ?? 1,
            item_id: product?.item_id,
            quantity: form.qty,
            line_address:
                product?.cf_vendor_address ??
                product?.custom_field_hash?.cf_vendor_address ??
                '77 opebi road',
            cf_city:
                product?.cf_vendor_city ?? product?.custom_field_hash?.cf_vendor_city ?? 'ikeja',
            cf_state:
                product?.cf_vendor_state ?? product?.custom_field_hash?.cf_vendor_state ?? 'lagos',
            cf_country:
                product?.cf_vendor_country ?? product?.custom_field_hash?.cf_vendor_country ?? 'NG',
        },
    ];

    const handleSubmit = async () => {
        if (!form.paymentMethod) {
            toast.error('Please choose a payment option');
            return;
        }

        const finalAddr = deliveryAddress || {
            first_name: userAttrs?.first_name ?? '',
            last_name: userAttrs?.last_name ?? '',
            email: flattenedUser?.email || '',
            phone: flattenedUser?.phone || '',
            line1: userAttrs?.address || '',
            city: userAttrs?.city || '',
            state: userAttrs?.state || '',
            country: userAttrs?.country || '',
            is_residential: true,
        };

        const payload = {
            products: [
                {
                    item_id: product?.item_id,
                    qty: form.qty,
                    reason: form.reason,
                    color: form.color,
                    size: form.size,
                    product_name: productName,
                    product_image: productImage ?? '',
                    vendor_id:
                        product?.cf_vendor_id ?? product?.custom_field_hash?.cf_vendor_id ?? null,
                    vendor_name:
                        product?.cf_vendor_name ??
                        product?.custom_field_hash?.cf_vendor_name ??
                        null,
                    sku: product?.sku ?? null,
                    rate: unitPrice,
                    unit: product?.unit ?? null,
                    brand: product?.brand ?? null,
                    category_id: product?.category_id ?? null,
                    category_name: product?.category_name ?? null,
                    is_grouped: product?.is_grouped ?? false,
                    group_id: product?.group_id ?? null,
                    group_name: product?.group_name ?? itemGroup?.group_name ?? null,
                    cf_singleimage:
                        product?.cf_singleimage ??
                        product?.custom_field_hash?.cf_singleimage ??
                        null,
                    cf_productimages:
                        product?.cf_productimages ??
                        product?.custom_field_hash?.cf_productimages ??
                        null,
                },
            ],
            customer_id: customerContext?.customer_id ?? userAttrs?.zoho_id ?? null,
            delivery_address: {
                ...finalAddr,
                metadata: {
                    customerId: customerContext?.customer_id ?? userAttrs?.zoho_id,
                    currency: { symbol: selectedCurrency?.symbol, rate: selectedCurrency?.sell },
                    customerName: `${finalAddr.first_name} ${finalAddr.last_name}`.trim(),
                    line1: finalAddr.line1,
                    city: finalAddr.city,
                    state:
                        typeof finalAddr.state === 'object'
                            ? finalAddr.state?.label
                            : finalAddr.state,
                    country:
                        typeof finalAddr.country === 'object'
                            ? finalAddr.country?.label
                            : finalAddr.country,
                    line_items: [
                        {
                            item_id: product?.item_id,
                            product_name: productName,
                            product_image: productImage ?? '',
                            vendor_id: product?.cf_vendor_id ?? null,
                            vendor_name: product?.cf_vendor_name ?? null,
                            quantity: form.qty,
                            color: form.color,
                            size: form.size,
                            reason: form.reason,
                            sku: product?.sku ?? '',
                            rate: unitPrice,
                        },
                    ],
                },
            },
            dispatcher,
            currency: { symbol: selectedCurrency?.symbol, rate: selectedCurrency?.sell },
            amount: convertedTotal,
            carrier_id: dispatcher?.carrier_id ?? 'CA-EMBFYYC4LO4IHFTW',
            payment_method: form.paymentMethod,
        };

        try {
            const response = await mutate(payload);
            if (!response?.success) throw new Error(response?.message ?? 'Request failed');

            if (form.paymentMethod === 'PAYSTACK') {
                const popup = new Paystack();
                popup.checkout({
                    key: process.env.REACT_APP_PAYSTACK_KEY,
                    email: flattenedUser?.email,
                    currency: usdToNaira?.symbol,
                    amount: Math.round((usdToNaira?.price ?? 0) * 100),
                    paymentMethods: ['card'],
                    reference: response?.order_ref,
                    metadata: { orderRef: response?.order_ref },
                    onSuccess: () => {
                        toast.success('Payment successful!');
                        setDone(true);
                    },
                    onCancel: () => toast.error('Payment cancelled'),
                    onError: (err) => toast.error(err.message),
                });
            } else {
                setDone(true);
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.message ?? 'Failed to submit sample request. Please try again.');
        }
    };

    if (!sampleAvailable) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
                <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl" border={`1px solid ${BORDER}`} mx={3}>
                    <ModalCloseButton />
                    <ModalBody py={10} px={6} textAlign="center">
                        <Text fontSize="3xl" mb={3}>
                            🚫
                        </Text>
                        <Text fontWeight="800" fontSize="lg" color={NAVY} mb={2}>
                            Samples Not Available
                        </Text>
                        <Text fontSize="sm" color="gray.500" lineHeight="1.6">
                            The vendor does not currently allow sample requests for{' '}
                            <Text as="span" fontWeight="700" color={NAVY}>
                                {productName}
                            </Text>
                            .
                        </Text>
                        <Button
                            mt={6}
                            bg={NAVY}
                            color="white"
                            _hover={{ bg: NAVY2 }}
                            borderRadius="xl"
                            w="full"
                            onClick={onClose}>
                            Got it
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    if (done) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
                <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl" border={`1px solid ${BORDER}`} mx={3}>
                    <ModalCloseButton />
                    <ModalBody py={10} px={6} textAlign="center">
                        <Text fontSize="4xl" mb={3}>
                            🎉
                        </Text>
                        <Text
                            fontWeight="800"
                            fontSize="xl"
                            color={NAVY}
                            mb={2}
                            letterSpacing="-0.02em">
                            Request Submitted!
                        </Text>
                        <Text fontSize="sm" color="gray.500" lineHeight="1.6" mb={6}>
                            Your sample request for{' '}
                            <Text as="span" fontWeight="700" color={NAVY}>
                                {productName}
                            </Text>{' '}
                            has been received. We'll get back to you shortly.
                        </Text>
                        <Button
                            bg={NAVY}
                            color="white"
                            _hover={{ bg: NAVY2 }}
                            borderRadius="xl"
                            w="full"
                            onClick={onClose}>
                            Close
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            scrollBehavior="inside"
            size={{ base: 'full', md: 'xl' }}>
            <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
            <ModalContent
                borderRadius={{ base: '2xl 2xl 0 0', md: '2xl' }}
                border={`1px solid ${BORDER}`}
                shadow="2xl"
                maxH={{ base: '92vh', md: '90vh' }}
                mx={{ base: 0, md: 'auto' }}
                mb={{ base: 0, md: 'auto' }}
                mt={{ base: 'auto', md: 'auto' }}
                position={{ base: 'fixed', md: 'relative' }}
                bottom={{ base: 0, md: 'auto' }}
                left={{ base: 0, md: 'auto' }}
                right={{ base: 0, md: 'auto' }}
                w={{ base: '100%', md: 'auto' }}>
                <ModalHeader
                    bg={NAVY}
                    color="white"
                    borderTopRadius={{ base: '2xl', md: '2xl' }}
                    py={{ base: 4, md: 5 }}
                    px={{ base: 5, md: 6 }}>
                    <Text
                        fontSize={{ base: 'sm', md: 'md' }}
                        fontWeight="800"
                        letterSpacing="-0.01em">
                        Request a Sample
                    </Text>
                    <Text fontSize="xs" opacity={0.65} mt="2px" fontWeight="400">
                        {productName}
                    </Text>
                </ModalHeader>
                <ModalCloseButton color="white" top={{ base: 3, md: 4 }} />

                <ModalBody px={{ base: 4, md: 6 }} py={{ base: 4, md: 5 }}>
                    {step === 0 && (
                        <Box>
                            <Box mb={5}>
                                <Text
                                    fontWeight="800"
                                    fontSize="md"
                                    color={NAVY}
                                    letterSpacing="-0.01em">
                                    Who is this for?
                                </Text>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Select an existing customer, create a new one, or continue as
                                    yourself
                                </Text>
                            </Box>
                            <CustomerStep
                                onConfirm={(ctx) => {
                                    setCustomerContext(ctx);
                                    setStep(1);
                                }}
                                onSkip={() => {
                                    setCustomerContext(null);
                                    setStep(1);
                                }}
                            />
                        </Box>
                    )}

                    {step >= 1 && (
                        <StepBar current={step} steps={['Product', 'Delivery', 'Payment']} />
                    )}

                    {step >= 1 && customerContext?.customer && (
                        <Flex
                            align="center"
                            gap={2}
                            mb={4}
                            p={3}
                            bg="#EEF3F9"
                            borderRadius="xl"
                            border={`1px solid ${BORDER}`}>
                            <Text fontSize="xs" color={NAVY} fontWeight="700">
                                👤
                            </Text>
                            <Text fontSize="xs" color="gray.600" flex={1} noOfLines={1}>
                                {customerContext.customer.first_name}{' '}
                                {customerContext.customer.last_name}
                                {customerContext.customer.email
                                    ? ` · ${customerContext.customer.email}`
                                    : ''}
                            </Text>
                            <Button
                                ml="auto"
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => {
                                    setCustomerContext(null);
                                    setStep(0);
                                }}>
                                Change
                            </Button>
                        </Flex>
                    )}

                    {step === 1 && (
                        <VStack spacing={4} align="stretch">
                            <Flex
                                gap={3}
                                align="center"
                                bg={SLATE}
                                borderRadius="xl"
                                p={3}
                                border={`1px solid ${BORDER}`}>
                                <Image
                                    src={productImage ?? PlaceHolder}
                                    fallbackSrc={PlaceHolder}
                                    w={{ base: '56px', md: '72px' }}
                                    h={{ base: '56px', md: '72px' }}
                                    objectFit="cover"
                                    borderRadius="xl"
                                    border={`2px solid ${BORDER}`}
                                    flexShrink={0}
                                />
                                <Box flex={1} minW={0}>
                                    <Text
                                        fontWeight="800"
                                        fontSize="sm"
                                        color={NAVY}
                                        noOfLines={2}
                                        lineHeight="1.3"
                                        mb={1}>
                                        {productName}
                                    </Text>
                                    <Text fontSize="xs" color="gray.400" fontFamily="mono" mb={1}>
                                        {product?.sku}
                                    </Text>
                                    <Badge colorScheme="green" fontSize="9px" borderRadius="full">
                                        ✓ Sample Available
                                    </Badge>
                                </Box>
                                <Box textAlign="right" flexShrink={0}>
                                    <Text fontSize="10px" color="gray.400" mb="1px">
                                        Unit Price
                                    </Text>
                                    <Text fontWeight="800" color={TEAL} fontSize="sm">
                                        <WalletHistoryPrice price={unitPrice} />
                                    </Text>
                                </Box>
                            </Flex>

                            <Box>
                                <FieldLabel>Quantity *</FieldLabel>
                                <Flex align="center" gap={3}>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled
                                        borderRadius="lg"
                                        onClick={() => set('qty', Math.max(1, form.qty - 1))}>
                                        −
                                    </Button>
                                    <Input
                                        size="sm"
                                        type="number"
                                        min={1}
                                        w="70px"
                                        textAlign="center"
                                        borderRadius="lg"
                                        disabled
                                        bg={SLATE}
                                        border={`1px solid ${BORDER}`}
                                        _focus={{ borderColor: TEAL }}
                                        value={form.qty}
                                        onChange={(e) =>
                                            set('qty', Math.max(1, parseInt(e.target.value) || 1))
                                        }
                                    />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        borderRadius="lg"
                                        disabled
                                        onClick={() => set('qty', form.qty + 1)}>
                                        +
                                    </Button>
                                    <Text fontSize="xs" color="gray.400">
                                        ≈ <WalletHistoryPrice price={unitPrice * form.qty} /> total
                                    </Text>
                                </Flex>
                            </Box>

                            {(sizeOptions.length > 0 || colorOptions.length > 0) && (
                                <SimpleGrid columns={2} gap={3}>
                                    {sizeOptions.length > 0 && (
                                        <Box>
                                            <FieldLabel>Size</FieldLabel>
                                            <Select
                                                size="sm"
                                                borderRadius="lg"
                                                bg={SLATE}
                                                border={`1px solid ${BORDER}`}
                                                _focus={{ borderColor: TEAL }}
                                                value={form.size}
                                                disabled
                                                onChange={(e) => set('size', e.target.value)}>
                                                <option value="">Select size</option>
                                                {sizeOptions.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Box>
                                    )}
                                    {colorOptions.length > 0 && (
                                        <Box>
                                            <FieldLabel>Colour</FieldLabel>
                                            <Select
                                                size="sm"
                                                borderRadius="lg"
                                                bg={SLATE}
                                                disabled
                                                border={`1px solid ${BORDER}`}
                                                _focus={{ borderColor: TEAL }}
                                                value={form.color}
                                                onChange={(e) => set('color', e.target.value)}>
                                                <option value="">Select colour</option>
                                                {colorOptions.map((c) => (
                                                    <option key={c} value={c}>
                                                        {c}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Box>
                                    )}
                                </SimpleGrid>
                            )}

                            <Box>
                                <FieldLabel>Reason for Request *</FieldLabel>
                                <Textarea
                                    size="sm"
                                    borderRadius="lg"
                                    bg={SLATE}
                                    border={`1px solid ${BORDER}`}
                                    minH="80px"
                                    _focus={{ borderColor: TEAL }}
                                    placeholder="e.g., I'm interested in ordering 200 units for my store and would like to check the quality first."
                                    value={form.reason}
                                    onChange={(e) => set('reason', e.target.value)}
                                />
                                <Text fontSize="10px" color="gray.400" mt={1}>
                                    The vendor uses this to evaluate your request
                                </Text>
                            </Box>

                            <Flex gap={3} justify="space-between" pt={1}>
                                <Button
                                    variant="outline"
                                    borderRadius="xl"
                                    size="sm"
                                    onClick={() => setStep(0)}>
                                    ← Back
                                </Button>
                                <Button
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY2 }}
                                    borderRadius="xl"
                                    flex={1}
                                    isDisabled={!form.reason.trim()}
                                    onClick={() => setStep(2)}>
                                    Next: Delivery →
                                </Button>
                            </Flex>
                        </VStack>
                    )}

                    {step === 2 && (
                        <DispatchStep
                            deliveryAddress={deliveryAddress}
                            cartItems={cartItemsForDispatch}
                            onConfirm={(disp) => {
                                setDispatcher(disp);
                                setStep(3);
                            }}
                            onBack={() => setStep(1)}
                        />
                    )}

                    {step === 3 && (
                        <VStack spacing={4} align="stretch">
                            <Flex
                                gap={3}
                                align="center"
                                bg={NAVY}
                                borderRadius="xl"
                                p={3}
                                color="white">
                                <Image
                                    src={productImage ?? PlaceHolder}
                                    fallbackSrc={PlaceHolder}
                                    w="40px"
                                    h="40px"
                                    objectFit="cover"
                                    borderRadius="lg"
                                    border="2px solid rgba(255,255,255,0.2)"
                                    flexShrink={0}
                                />
                                <Box flex={1} minW={0}>
                                    <Text fontSize="xs" fontWeight="700" noOfLines={1}>
                                        {productName}
                                    </Text>
                                    <Text fontSize="10px" opacity={0.65}>
                                        Qty: {form.qty}
                                        {form.size ? ` · ${form.size}` : ''}
                                        {form.color ? ` · ${form.color}` : ''}
                                    </Text>
                                </Box>
                                <Box textAlign="right" flexShrink={0}>
                                    <Text fontSize="10px" opacity={0.65}>
                                        Total
                                    </Text>
                                    <Text fontWeight="800" fontSize="sm">
                                        {selectedCurrency?.symbol}
                                        {convertedTotal.toLocaleString(undefined, {
                                            maximumFractionDigits: 0,
                                        })}
                                    </Text>
                                </Box>
                            </Flex>

                            {dispatcher && (
                                <Flex
                                    align="center"
                                    gap={3}
                                    p={3}
                                    bg="#EEF3F9"
                                    borderRadius="xl"
                                    border={`1px solid ${BORDER}`}>
                                    <Text fontSize="lg">
                                        {dispatcher.dispatchType === 'pickup' ? '🏢' : '🚚'}
                                    </Text>
                                    <Box flex={1}>
                                        <Text fontSize="xs" fontWeight="700" color={NAVY}>
                                            {dispatcher.carrier_name}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {dispatcher.carrier_rate_description}
                                        </Text>
                                    </Box>
                                    <Box textAlign="right">
                                        <Text fontSize="10px" color="gray.400">
                                            Delivery fee
                                        </Text>
                                        <Text fontSize="sm" fontWeight="700" color={NAVY}>
                                            {dispatcher.amount > 0
                                                ? `₦${dispatcher.amount.toLocaleString()}`
                                                : 'Free'}
                                        </Text>
                                    </Box>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="blue"
                                        onClick={() => setStep(2)}>
                                        Change
                                    </Button>
                                </Flex>
                            )}

                            {deliveryAddress?.line1 && (
                                <Box
                                    p={3}
                                    bg={SLATE}
                                    borderRadius="xl"
                                    border={`1px solid ${BORDER}`}>
                                    <Text
                                        fontSize="10px"
                                        color="gray.400"
                                        fontWeight="700"
                                        textTransform="uppercase"
                                        letterSpacing="0.06em"
                                        mb={1}>
                                        Delivery Address
                                    </Text>
                                    <Text fontSize="xs" color={NAVY} fontWeight="600">
                                        {[
                                            deliveryAddress.line1,
                                            deliveryAddress.city,
                                            typeof deliveryAddress.state === 'object'
                                                ? deliveryAddress.state?.label
                                                : deliveryAddress.state,
                                            typeof deliveryAddress.country === 'object'
                                                ? deliveryAddress.country?.label
                                                : deliveryAddress.country,
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </Text>
                                </Box>
                            )}

                            <Divider borderColor={BORDER} />

                            <Text
                                fontWeight="700"
                                color={NAVY}
                                fontSize="xs"
                                textTransform="uppercase"
                                letterSpacing="0.06em">
                                Payment Option
                            </Text>

                            <RadioGroup
                                value={form.paymentMethod}
                                onChange={(val) => set('paymentMethod', val)}>
                                <VStack align="stretch" spacing={2}>
                                    {[
                                        {
                                            val: 'POD',
                                            icon: '💵',
                                            title: 'Pay on Delivery / Pickup',
                                            sub: 'Pay when your order arrives',
                                        },
                                        {
                                            val: 'PAYSTACK',
                                            icon: '💳',
                                            title: 'Pay with Paystack',
                                            sub: 'Card or bank account',
                                        },
                                        {
                                            val: 'WALLET',
                                            icon: '👛',
                                            title: 'Pay with Wallet',
                                            sub: 'Use your wallet balance',
                                        },
                                    ].map((opt) => {
                                        const active = form.paymentMethod === opt.val;
                                        return (
                                            <Box
                                                key={opt.val}
                                                border="2px solid"
                                                borderColor={active ? NAVY : BORDER}
                                                p={3}
                                                borderRadius="xl"
                                                cursor="pointer"
                                                bg={active ? '#EEF3F9' : 'white'}
                                                _hover={{ borderColor: NAVY, bg: '#EEF3F9' }}
                                                transition="all 0.15s"
                                                onClick={() => set('paymentMethod', opt.val)}>
                                                <HStack spacing={3}>
                                                    <Text fontSize="lg">{opt.icon}</Text>
                                                    <Radio value={opt.val} colorScheme="blue" />
                                                    <Box flex={1}>
                                                        <Text
                                                            fontWeight="700"
                                                            fontSize="sm"
                                                            color={active ? NAVY : 'gray.700'}>
                                                            {opt.title}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">
                                                            {opt.sub}
                                                        </Text>
                                                    </Box>
                                                    {active && <CheckCircleIcon color={NAVY} />}
                                                </HStack>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </RadioGroup>

                            <Flex gap={3} justify="space-between" pt={1}>
                                <Button
                                    variant="outline"
                                    borderRadius="xl"
                                    size="sm"
                                    onClick={() => setStep(2)}>
                                    ← Back
                                </Button>
                                <Button
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY2 }}
                                    borderRadius="xl"
                                    flex={1}
                                    isLoading={isPending}
                                    isDisabled={isPending || !form.paymentMethod}
                                    onClick={handleSubmit}>
                                    Submit Sample Request
                                </Button>
                            </Flex>
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
